import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// Exchange Rate Types
export interface ExchangeRate {
  _id: string;
  baseCurrency: 'LAK' | 'THB' | 'USD';
  targetCurrency: 'LAK' | 'THB' | 'USD';
  rate: number;
  updatedBy: {
    _id: string;
    name: string;
    employeeId: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExchangeRateData {
  [key: string]: {
    rate: number;
    updatedAt: string;
    updatedBy: {
      _id: string;
      name: string;
      employeeId: string;
    };
  };
}

export interface ConversionResult {
  originalAmount: number;
  fromCurrency: string;
  toCurrency: string;
  convertedAmount: number;
  exchangeRate: number;
  calculatedAt: string;
}

// Request Interfaces
export interface UpdateExchangeRateRequest {
  rates: {
    [key: string]: number; // เช่น "LAK_USD": 0.000045
  };
}

export interface ConvertCurrencyRequest {
  amount: number;
  fromCurrency: 'LAK' | 'THB' | 'USD';
  toCurrency: 'LAK' | 'THB' | 'USD';
}

export interface GetExchangeRateHistoryParams {
  baseCurrency?: 'LAK' | 'THB' | 'USD';
  targetCurrency?: 'LAK' | 'THB' | 'USD';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// Exchange Rate Service
export const exchangeRateService = {
  // ดึงอัตราแลกเปลี่ยนปัจจุบัน
  getExchangeRates: async (): Promise<ApiResponse<ExchangeRateData>> => {
    const response = await api.get('/exchange-rates');

    return response.data;
  },

  // ແກ້ໄຂอัตราแลกเปลี่ยน
  updateExchangeRate: async (data: UpdateExchangeRateRequest): Promise<ApiResponse<ExchangeRate[]>> => {
    const response = await api.put('/exchange-rates', data);
    return response.data;
  },

  // ดูประวัติอัตราแลกเปลี่ยน
  getExchangeRateHistory: async (params: GetExchangeRateHistoryParams = {}): Promise<PaginatedResponse<ExchangeRate>> => {
    const response = await api.get('/exchange-rates/history', { params });
    return response.data;
  },

  // คำนวณการแปลงสกุลเงิน
  calculateConversion: async (data: ConvertCurrencyRequest): Promise<ApiResponse<ConversionResult>> => {
    const response = await api.post('/exchange-rates/convert', data);
    return response.data;
  },

  // Helper methods สำหรับใช้งานเฉพาะ

  // ดึงอัตราแลกเปลี่ยนเฉพาะคู่สกุลเงิน
  getSpecificRate: async (baseCurrency: string, targetCurrency: string): Promise<number | null> => {
    try {
      const response = await exchangeRateService.getExchangeRates();
      const key = `${baseCurrency}_${targetCurrency}`;
      return response.data[key]?.rate || null;
    } catch (error) {
      console.error('Error getting specific rate:', error);
      return null;
    }
  },

  // ແກ້ໄຂอัตราเดียว
  updateSingleRate: async (baseCurrency: string, targetCurrency: string, rate: number): Promise<ApiResponse<ExchangeRate[]>> => {
    const key = `${baseCurrency}_${targetCurrency}`;
    return exchangeRateService.updateExchangeRate({
      rates: { [key]: rate }
    });
  },

  // คำนวณการแปลงแบบง่าย
  convertAmount: async (amount: number, from: 'LAK' | 'THB' | 'USD', to: 'LAK' | 'THB' | 'USD'): Promise<number> => {
    try {
      const response = await exchangeRateService.calculateConversion({
        amount,
        fromCurrency: from,
        toCurrency: to
      });
      return response.data.convertedAmount;
    } catch (error) {
      console.error('Error converting amount:', error);
      return 0;
    }
  },

  // ดึงประวัติการเปลี่ยนแปลงของคู่สกุลเงิน
  getCurrencyPairHistory: async (baseCurrency: string, targetCurrency: string, days: number = 30): Promise<PaginatedResponse<ExchangeRate>> => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    return exchangeRateService.getExchangeRateHistory({
      baseCurrency: baseCurrency as 'LAK' | 'THB' | 'USD',
      targetCurrency: targetCurrency as 'LAK' | 'THB' | 'USD',
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  },

  // รับอัตราแลกเปลี่ยนทั้งหมดในรูปแบบ object
  getAllRatesAsObject: async (): Promise<{ [key: string]: number }> => {
    try {
      const response = await exchangeRateService.getExchangeRates();
      const rates: { [key: string]: number } = {};
      
      Object.entries(response.data).forEach(([key, value]) => {
        rates[key] = value.rate;
      });
      
      return rates;
    } catch (error) {
      console.error('Error getting all rates:', error);
      return {};
    }
  },

  // ตรวจสอบว่าอัตราแลกเปลี่ยนล่าสุดหรือไม่
  isRateUpToDate: (updatedAt: string, maxAgeMinutes: number = 60): boolean => {
    const rateDate = new Date(updatedAt);
    const now = new Date();
    const diffMinutes = (now.getTime() - rateDate.getTime()) / (1000 * 60);
    return diffMinutes <= maxAgeMinutes;
  },

  // ฟอร์แมตอัตราแลกเปลี่ยนสำหรับแสดงผล
  formatRate: (rate: number, decimals: number = 6): string => {
    return rate.toFixed(decimals);
  },

  // แปลงชื่อสกุลเงินเป็นสัญลักษณ์
  getCurrencySymbol: (currency: string): string => {
    const symbols = {
      LAK: '₭',
      THB: '฿',
      USD: '$'
    };
    return symbols[currency as keyof typeof symbols] || currency;
  },

  // แปลงชื่อสกุลเงินเป็นชื่อเต็ม
  getCurrencyFullName: (currency: string): string => {
    const names = {
      LAK: 'Lao Kip',
      THB: 'Thai Baht',
      USD: 'US Dollar'
    };
    return names[currency as keyof typeof names] || currency;
  },

  // รับคู่สกุลเงินที่สำคัญ
  getMainCurrencyPairs: (): Array<{ base: string; target: string; label: string }> => {
    return [
      { base: 'LAK', target: 'USD', label: 'LAK → USD' },
      { base: 'USD', target: 'LAK', label: 'USD → LAK' },
      { base: 'THB', target: 'USD', label: 'THB → USD' },
      { base: 'USD', target: 'THB', label: 'USD → THB' },
      { base: 'LAK', target: 'THB', label: 'LAK → THB' },
      { base: 'THB', target: 'LAK', label: 'THB → LAK' },
    ];
  },

  // คำนวณการเปลี่ยนแปลงเปอร์เซ็นต์
  calculatePercentageChange: (oldRate: number, newRate: number): number => {
    if (oldRate === 0) return 0;
    return ((newRate - oldRate) / oldRate) * 100;
  },

  // ตรวจสอบความผิดปกติของอัตราแลกเปลี่ยน
  detectAbnormalRate: (rate: number, baseCurrency: string, targetCurrency: string): boolean => {
    // กำหนดช่วงที่สมเหตุสมผลสำหรับแต่ละคู่สกุลเงิน
    const normalRanges = {
      'LAK_USD': { min: 0.00001, max: 0.0001 },
      'USD_LAK': { min: 10000, max: 100000 },
      'THB_USD': { min: 0.02, max: 0.04 },
      'USD_THB': { min: 25, max: 50 },
      'LAK_THB': { min: 0.0001, max: 0.01 },
      'THB_LAK': { min: 100, max: 10000 }
    };

    const key = `${baseCurrency}_${targetCurrency}`;
    const range = normalRanges[key as keyof typeof normalRanges];
    
    if (!range) return false;
    return rate < range.min || rate > range.max;
  }
};